import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/auth/profile');
      setProfile(res.data);
    } catch (err) {
      setError(t('profile.failedToLoadProfile'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('profile.passwordsNotMatch'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('profile.passwordTooShort'));
      return;
    }

    try {
      await api.put('/auth/profile', { password: formData.password });
      setSuccess(t('profile.passwordUpdated'));
      setFormData({ password: '', confirmPassword: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || t('profile.failedToUpdatePassword'));
    }
  };

  if (loading) {
    return <Container>{t('common.loading')}</Container>;
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" gutterBottom>
        {t('profile.title')}
      </Typography>
      <Paper sx={{ p: 3, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          {t('profile.accountInfo')}
        </Typography>
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label={t('profile.fullName')}
            value={profile?.full_name || ''}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('profile.email')}
            value={profile?.email || ''}
            disabled
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label={t('profile.phone')}
            value={profile?.phone || ''}
            disabled
          />
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
          {t('profile.changePassword')}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <Box component="form" onSubmit={handlePasswordChange}>
          <TextField
            fullWidth
            type="password"
            label={t('profile.newPassword')}
            value={formData.password}
            onChange={(e) =>
              setFormData({ ...formData, password: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            type="password"
            label={t('profile.confirmPassword')}
            value={formData.confirmPassword}
            onChange={(e) =>
              setFormData({ ...formData, confirmPassword: e.target.value })
            }
            sx={{ mb: 2 }}
          />
          <Button type="submit" variant="contained">
            {t('profile.updatePassword')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;



